import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  useMediaQuery,
  Box,
  Grid2 as Grid,
  CardActionArea,
  CardActions,
  Button,
} from '@mui/material';
import { Add, CallSplit, Undo } from '@mui/icons-material';
import { getNewDateTime, renderFieldValue } from '../../utils/utils';
import { Actions, useDispatchProvider, useStateFormOrder } from '../utils/StateProvider';
import { addDoc, collection, doc, getDoc, query, updateDoc, where } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import { useCollection } from 'react-firebase-hooks/firestore';
import ListHeaderLabel from '../utils/ListHeaderLabel';
import SkeletonList from '../utils/SkeletonList';
import { createPortal } from 'react-dom';
import ListNoResults from '../utils/ListNoResults';
import { useForm } from 'react-hook-form';
import FieldCheckbox from '../fields/FieldCheckbox';
import { usePermissions } from '../utils/AclProvider';

const ListOrderProducts = ({ portalRef }) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const dispatch = useDispatchProvider();
  const permissions = usePermissions();
  const stateFormOrder = useStateFormOrder();
  const [stateOrderSplit, setStateOrderSplit] = useState(false);
  const handleDialogProduct = (productId) => () => {
    dispatch && dispatch({ type: Actions.FormProduct, payload: productId });
    dispatch && dispatch({ type: Actions.DialogProduct, payload: true });
  };
  const handleStateOrderSplit = (value) => () => {
    if (!value) {
      reset();
    }
    setStateOrderSplit(value);
  };
  const columns = [
    { id: 'description', label: 'Description' },
    { id: 'supplierRef', label: 'Supplier' },
    { id: 'productStatus', label: 'Status' },
    // { id: 'orderRef', label: 'Order' },
    { id: 'dateCreated', label: 'Date created' },
    { id: 'dateOrdered', label: 'Date ordered' },
    { id: 'dateReceived', label: 'Date received' },
    { id: 'price', label: 'Price', align: 'right' },
  ];

  const { control, getValues, reset } = useForm({
    defaultValues: {},
  });

  // const collectionRef = query(collection(db, 'products'));
  // const collectionRef = collection(db, 'products');
  const orderRef = doc(db, 'orders', stateFormOrder);
  const collectionRef = query(collection(db, 'products'), where('orderRef', '==', orderRef));

  const [productsSnapshot, loading, error] = useCollection(collectionRef, {
    snapshotListenOptions: { includeMetadataChanges: true },
  });

  const calculateTotal = () => {
    return productsSnapshot.data().reduce((partialSum, product) => partialSum + parseFloat(product.price), 0);
  };

  const handleSplitOrder = async () => {
    try {
      const ordersDocRef = collection(db, 'orders');
      const orderRef = doc(db, 'orders', stateFormOrder);
      const orderSnap = await getDoc(orderRef);

      let orderData = orderSnap.data();
      const productForm = getValues();

      const newOrderDocRef = await addDoc(ordersDocRef, {
        ...orderData,
        dateCreated: getNewDateTime(),
        userCreated: auth.currentUser.displayName,
        dateUpdated: '',
        dateCompleted: '',
        userUpdated: '',
        userCompleted: '',
      });

      const productsData = await Promise.all(
        Object.keys(productForm)
          .filter((product) => productForm[product])
          .map(async (productId) => {
            const productDocRef = doc(db, 'products', productId);
            await updateDoc(productDocRef, {
              orderRef: newOrderDocRef,
            });
          }),
      );

      reset();

      dispatch && dispatch({ type: Actions.FormOrder, payload: newOrderDocRef.id });
      dispatch &&
        dispatch({
          type: Actions.Snackbar,
          payload: {
            open: true,
            severity: 'success',
            message: `Success splitting order: ${newOrderDocRef.id}`,
          },
        });
    } catch (err) {
      // console.log(err);
      dispatch &&
        dispatch({
          type: Actions.Snackbar,
          payload: {
            open: true,
            severity: 'error',
            message: `Error splitting order: ${err}`,
          },
        });
    }
  };

  // useEffect(() => {
  //   if (!loading && productsSnapshot.docs().length > 0) {
  //     setStateOrderProducts((prevState) => {
  //       return {
  //         total: calculateTotal(),
  //         products: productsSnapshot.docs().length,
  //       };
  //     });
  //   }
  // }, [setStateOrdersChange, loading, productsSnapshot]);

  useEffect(() => {
    if (error) {
      dispatch &&
        dispatch({
          type: Actions.Snackbar,
          payload: {
            open: true,
            severity: 'error',
            message: `Error fetching orders collection: ${error}`,
          },
        });
    }
  }, [error]);

  const mapColumnGridXs = (column) => {
    switch (column.id) {
      case 'dateCreated':
      case 'dateOrdered':
      case 'dateReceived':
      case 'productStatus':
        return 4;
      case 'supplierRef':
        return 8;
      default:
        return 12;
    }
  };

  const mapColumnGridMd = (column) => {
    switch (column.id) {
      case 'description':
        return 4;
      default:
        return 'grow';
    }
  };

  return (
    <Box sx={{ overflowY: 'auto', height: '100%' }}>
      <>
        {!isMobile && (
          <Box
            position={'sticky'}
            top={0}
            background={'background.surface'}
            zIndex={1}
          >
            <Card
              elevation={0}
              sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center' }}
            >
              {stateOrderSplit && (
                <CardActions
                  sx={{
                    paddingLeft: 2,
                    flexDirection: isMobile ? 'row' : 'column',
                    '>:not(style)~:not(style)': { paddingLeft: 1 },
                  }}
                >
                  <Box sx={{ width: 20 }} />
                </CardActions>
              )}
              <CardContent sx={{ px: 2, py: 2, flexGrow: 1, '&:last-child': { pb: 2 } }}>
                <Grid
                  container
                  spacing={2}
                >
                  {columns.map((column) => (
                    <Grid
                      size={mapColumnGridMd(column)}
                      key={column.id}
                    >
                      <ListHeaderLabel column={column} />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}
        <Grid
          container
          spacing={2}
        >
          {loading && <SkeletonList />}
          {!loading && productsSnapshot?.docs.length === 0 && <ListNoResults />}
          {!loading &&
            productsSnapshot?.docs.map((product) => {
              return (
                <Grid
                  size={12}
                  key={product.id}
                >
                  <Card
                    data-id={product.id}
                    sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center' }}
                  >
                    {stateOrderSplit && (
                      <CardActions
                        sx={{
                          alignSelf: isMobile ? 'flex-start' : 'center',
                          flexGrow: isMobile ? 1 : 0,
                          paddingLeft: 2,
                          '>:not(style)~:not(style)': { paddingLeft: 1 },
                        }}
                      >
                        <FieldCheckbox
                          label={isMobile ? 'Split product' : undefined}
                          control={control}
                          name={product.id}
                        />
                      </CardActions>
                    )}
                    <CardActionArea
                      onClick={handleDialogProduct(product.id)}
                      disabled={stateOrderSplit}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Grid
                          container
                          spacing={2}
                        >
                          {columns.map((column) => {
                            const productData = product.data();
                            return (
                              <Grid
                                size={{
                                  xs: mapColumnGridXs(column),
                                  md: mapColumnGridMd(column),
                                }}
                                key={column.id}
                              >
                                {isMobile && (
                                  <Typography
                                    color={'textSecondary'}
                                    variant={'body2'}
                                    align={isMobile ? 'left' : column.align}
                                  >
                                    {column.label}
                                  </Typography>
                                )}
                                {renderFieldValue(column.id, productData[column.id], isMobile ? 'left' : column.align)}
                              </Grid>
                            );
                          })}
                        </Grid>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
        </Grid>

        {!isMobile && (
          <Box
            position={'sticky'}
            bottom={0}
            background={'background.surface'}
            zIndex={1}
          >
            <Card elevation={0}>
              <CardContent sx={{ px: 2, py: 2, '&:last-child': { pb: 2 } }}>
                <Grid
                  container
                  spacing={2}
                >
                  {columns.map((column) => (
                    <Grid
                      size={'grow'}
                      key={column.id}
                    >
                      {column.id === 'price' && (
                        <Typography
                          color={'primary'}
                          variant={'subtitle2'}
                          sx={{ fontWeight: 'bold' }}
                          textAlign={column.align}
                        >
                          {/*{`Total: ${renderValueCurrency(stateOrderProducts.total)}`}*/}
                        </Typography>
                      )}
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}
        {portalRef.current &&
          createPortal(
            <>
              <Grid
                container
                spacing={3}
                justifyContent="flex-end"
              >
                {!stateOrderSplit && permissions?.orders?.delete && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'text'}
                      startIcon={<CallSplit />}
                      onClick={handleStateOrderSplit(true)}
                      size={isMobile ? 'small' : 'large'}
                    >
                      {/*{isMobile ? 'Split' : 'Split order'}*/}
                      Split order
                    </Button>
                  </Grid>
                )}
                {stateOrderSplit && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'text'}
                      startIcon={<Undo />}
                      onClick={handleStateOrderSplit(false)}
                      size={isMobile ? 'small' : 'large'}
                    >
                      {/*{isMobile ? 'Undo' : 'Undo order'}*/}
                      Undo order
                    </Button>
                  </Grid>
                )}
                {!stateOrderSplit && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'contained'}
                      startIcon={<Add />}
                      onClick={handleDialogProduct('')}
                      size={isMobile ? 'small' : 'large'}
                    >
                      {/*{isMobile ? 'Add' : 'Add product'}*/}
                      Add product
                    </Button>
                  </Grid>
                )}
                {stateOrderSplit && permissions?.orders?.delete && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'contained'}
                      startIcon={<CallSplit />}
                      onClick={handleSplitOrder}
                      size={isMobile ? 'small' : 'large'}
                    >
                      {/*{isMobile ? 'Split' : 'Split order'}*/}
                      Split order
                    </Button>
                  </Grid>
                )}
              </Grid>
            </>,
            // <Fab
            //   color="primary"
            //   aria-label="add"
            //   onClick={handleDialogProduct('')}
            // >
            //   <Add />
            // </Fab>,
            portalRef.current,
          )}
      </>
    </Box>
  );
};

export default ListOrderProducts;
